export type PopulationGroup = 'neonate' | 'pediatric' | 'adult';
export type CovariateScopeGroup = 'general' | PopulationGroup;
const NEONATE_MAX_AGE_DAYS = 28;
const ADULT_MIN_AGE_YEARS = 18;
const AGE_CONFLICT_TOLERANCE_YEARS = 0.25;
const NEONATAL_TRACK_KEYS = ['gestational_age', 'age_pna', 'age_pma'] as const;

export const POPULATION_GROUP_OPTIONS: Array<{ label: string; value: PopulationGroup; hint?: string }> = [
  { value: 'neonate', label: '新生儿' },
  { value: 'pediatric', label: '儿童' },
  { value: 'adult', label: '成人' },
];

export const COVARIATE_SCOPE_OPTIONS: Array<{ label: string; value: CovariateScopeGroup }> = [
  { value: 'general', label: '通用' },
  ...POPULATION_GROUP_OPTIONS,
];

export const POPULATION_GROUP_LABELS: Record<PopulationGroup, string> = {
  neonate: '新生儿',
  pediatric: '儿童',
  adult: '成人',
};

export const COVARIATE_SCOPE_LABELS: Record<CovariateScopeGroup, string> = {
  general: '通用',
  ...POPULATION_GROUP_LABELS,
};

export function getPopulationGroupLabel(group?: PopulationGroup | null): string {
  if (!group) return '';
  return POPULATION_GROUP_LABELS[group] || group;
}

export function getCovariateScopeLabel(group?: CovariateScopeGroup | null): string {
  if (!group) return '';
  return COVARIATE_SCOPE_LABELS[group] || group;
}

function toNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function inferPopulationGroup(values: Record<string, unknown>): PopulationGroup | null {
  const agePna = toNumber(values.age_pna);
  let ageYears = toNumber(values.age_years);
  const normalizedAgePna = (
    ageYears !== null
    && agePna !== null
    && ageYears >= 1
    && Math.abs(ageYears - (agePna / 365.25)) > AGE_CONFLICT_TOLERANCE_YEARS
  ) ? null : agePna;

  if (normalizedAgePna !== null && normalizedAgePna <= NEONATE_MAX_AGE_DAYS) return 'neonate';

  if (ageYears === null && normalizedAgePna !== null) {
    ageYears = normalizedAgePna / 365.25;
  }
  if (ageYears === null) return null;
  if (ageYears < 0) return null;
  if (ageYears <= (NEONATE_MAX_AGE_DAYS / 365.25)) return 'neonate';
  return ageYears >= ADULT_MIN_AGE_YEARS ? 'adult' : 'pediatric';
}

export function stripConflictingNeonatalCovariates(values: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...(values || {}) };
  const agePna = toNumber(normalized.age_pna);
  const ageYears = toNumber(normalized.age_years);

  if (
    ageYears !== null
    && agePna !== null
    && ageYears >= 1
    && Math.abs(ageYears - (agePna / 365.25)) > AGE_CONFLICT_TOLERANCE_YEARS
  ) {
    delete normalized.age_pna;
    delete normalized.age_pma;
    delete normalized.gestational_age;
    return normalized;
  }

  if (inferPopulationGroup(normalized) === 'adult') {
    NEONATAL_TRACK_KEYS.forEach((key) => {
      delete normalized[key];
    });
  }

  return normalized;
}
